import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, Input } from "@unified-checkout/design-system";
import { useEnterKeyPress } from "@app/dashboard/hooks/useEnterKeyPress";
import type { IField } from "@app/dashboard/types/outerLayout/common";
import {
  isFormValid,
  validateField as fieldValidator,
  passwordMatchValidation,
  passwordsComparisonLiteral,
  passwordValidationRules,
  passwordValidations,
  required,
} from "@app/dashboard/utils/formValidations";
import { BlueCheckMarkCircular, Spinner } from "@app/dashboard/assets/icons";
import { changePassword } from "@app/dashboard/services/outer_layout/change-password";
import { OUTER_ROUTES } from "@app/dashboard/routes/outer-routes";
import FormIntro from '@app/dashboard/components/outer_layout/FormIntro';
import styles from "./change-password.module.scss";



const SUBMIT_BTN_ID = 'change-password-form-submit-btn';

const STEPS = {
  CHANGE_PASSWORD: 1,
  SUCCESSFUL_PASSWORD_CHANGE: 2,
};

const FieldNames = {
  OLD_PASSWORD: "old_password",
  NEW_PASSWORD: "new_password",
  CONFIRM_PASSWORD: "confirm_password",
};

const fieldsInitialState = {
  [FieldNames.OLD_PASSWORD]: {
    value: '',
    isValid: undefined,
    errorMsg: '',
    validations: [required]
  },
  [FieldNames.NEW_PASSWORD]: {
    value: undefined,
    isValid: undefined,
    errorMsg: '',
    validations: passwordValidations
  },
  [FieldNames.CONFIRM_PASSWORD]: {
    value: undefined,
    isValid: undefined,
    errorMsg: '',
    validations: [required, passwordMatchValidation]
  }
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  useEnterKeyPress(SUBMIT_BTN_ID);

  const [loading, setLoading] = useState(false);
  const [serverErrMsg, setServerErrMsg] = useState('');
  const [fields, setFields] = useState<IField>(fieldsInitialState);
  const [showPasswordRules, setShowPasswordRules] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.CHANGE_PASSWORD);

  useEffect(() => {
    const passwordValue = fields[FieldNames.NEW_PASSWORD].value;
    const confirmPasswordValue = fields[FieldNames.CONFIRM_PASSWORD].value;

    typeof passwordValue === 'string' &&
      (validateField(FieldNames.NEW_PASSWORD, passwordValue), setShowPasswordRules(true));

    typeof confirmPasswordValue === 'string' && validateField(FieldNames.CONFIRM_PASSWORD, confirmPasswordValue);

  }, [fields[FieldNames.NEW_PASSWORD].value]);

  useEffect(() => {
    const confirmPasswordValue = fields[FieldNames.CONFIRM_PASSWORD].value;
    typeof confirmPasswordValue === 'string' && validateField(FieldNames.CONFIRM_PASSWORD, confirmPasswordValue);

  }, [fields[FieldNames.CONFIRM_PASSWORD].value]);

  const validateField = (fieldName: string, value: string) => {
    let valueToValidate = value;

    if (fieldName === FieldNames.CONFIRM_PASSWORD) {
      const passwordValue = fields[FieldNames.NEW_PASSWORD].value as string;
      const confirmPasswordValue = fields[FieldNames.CONFIRM_PASSWORD].value as string;

      const passwordsLiteral = passwordsComparisonLiteral(passwordValue, confirmPasswordValue);
      valueToValidate = passwordsLiteral;
    };

    fieldValidator(fields, setFields, fieldName, valueToValidate);
  };

  const onChangeHandler = (fieldName: string, value: string) => {
    setServerErrMsg("");

    setFields(prevState => {
      return ({
        ...prevState,
        [fieldName]: {
          ...prevState[fieldName],
          value: value,
        }
      })
    });

    validateField(fieldName, value);
  }

  const handleSubmit = () => {
    setServerErrMsg("");
    setLoading(true);

    const oldPassword = fields[FieldNames.OLD_PASSWORD].value as string;
    const newPassword = fields[FieldNames.NEW_PASSWORD].value as string;

    changePassword(oldPassword, newPassword)
      .then(() => {
        setCurrentStep(STEPS.SUCCESSFUL_PASSWORD_CHANGE);
      })
      .catch((error) => {
        const message = error.response?.data?.message;
        setServerErrMsg(error.response?.status === 400 ? message : t('errors.somethingWentWrong'));
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const redirectToLogin = () => {
    navigate(OUTER_ROUTES.LOGIN);
  };


  return (
    <FormIntro
      formLegend={t('outerLayout.form.changePassword.changePassReq')}
    >
      {
        currentStep === STEPS.CHANGE_PASSWORD &&
        <>
          <Input
            id={FieldNames.OLD_PASSWORD}
            name={FieldNames.OLD_PASSWORD}
            type='password'
            label={t('outerLayout.form.labels.password')}
            value={fields[FieldNames.OLD_PASSWORD].value}
            required
            onChange={(e) => { onChangeHandler(FieldNames.OLD_PASSWORD, e.target.value) }}
            status={fields[FieldNames.OLD_PASSWORD].isValid === false ? 'error' : 'default'}
            error={fields[FieldNames.OLD_PASSWORD].errorMsg}
            preventAutoComplete
            disabled={loading}
          />
          <Input
            id={FieldNames.NEW_PASSWORD}
            name={FieldNames.NEW_PASSWORD}
            type='password'
            label={t('outerLayout.form.changePassword.labels.newPassword')}
            value={fields[FieldNames.NEW_PASSWORD].value}
            {...(showPasswordRules ? { rules: passwordValidationRules } : {})}
            onChange={(e) => { onChangeHandler(FieldNames.NEW_PASSWORD, e.target.value) }}
            status={fields[FieldNames.NEW_PASSWORD].isValid === false ? 'error' : 'default'}
            preventAutoComplete
            disabled={loading}
          />
          <Input
            id={FieldNames.CONFIRM_PASSWORD}
            name={FieldNames.CONFIRM_PASSWORD}
            type='password'
            label={t('outerLayout.form.labels.confirmPassword')}
            value={fields[FieldNames.CONFIRM_PASSWORD].value}
            onChange={(e) => { onChangeHandler(FieldNames.CONFIRM_PASSWORD, e.target.value) }}
            status={fields[FieldNames.CONFIRM_PASSWORD].isValid === false ? 'error' : 'default'}
            error={fields[FieldNames.CONFIRM_PASSWORD].errorMsg}
            preventAutoComplete
            disabled={loading}
          />
          <div className='form__submit'>
            {serverErrMsg && <div className='form__submit__server-err'>{serverErrMsg}</div>}

            <Button
              id={SUBMIT_BTN_ID}
              size='md'
              color='primary'
              variant='contained'
              text={loading ? undefined : t('form.submit')}
              leadingIcon={loading ? Spinner : undefined}
              disabled={!isFormValid(fields) || loading}
              onClickHandler={handleSubmit}
            />
          </div>
        </>
      }
      {
        currentStep === STEPS.SUCCESSFUL_PASSWORD_CHANGE &&
        <div className={styles['successful-change-password']}>
          {BlueCheckMarkCircular}
          <p className={styles['successful-change-password__text']}>{t('outerLayout.form.changePassword.passwordUpdated')}</p>
          <Button
            size='md'
            color='primary'
            variant='contained'
            text={t('outerLayout.form.changePassword.login')}
            onClickHandler={redirectToLogin}
          />
        </div>
      }
    </FormIntro>
  )
}

export default ChangePassword;